import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import { useAuthUser } from "Components/Auth/hooks/useAuthUser";
import { useProfileLimits } from "Components/ProfileLimit/hooks/useProfileLimits";
import { getIsDayViewEnabled } from "Components/ProfileLimit/helpers/getIsDayViewEnabled";
import moment from "moment";
import LoadingIndicator from "Views/LoadingIndicator";
import { devLog } from "utils/developmentLogger";
import { LocalStorageKey } from "Helpers/LocalStorageHelper";

interface InitializationState {
	isReady: boolean;
	initialDate: moment.Moment;
	initialCalendarType: CalendarType;
	hasExplicitAttributes: boolean;
}

const InitializationContext = createContext<InitializationState>({
	isReady: false,
	initialDate: moment().startOf("day"),
	initialCalendarType: CalendarType.WEEK,
	hasExplicitAttributes: false,
});

interface Props {
	initialDate?: string;
	initialView?: string;
	children: React.ReactNode;
}

const PROFILE_LIMITS_TIMEOUT = 15000; // 15 seconds timeout - reasonable for Firestore

export const InitializationProvider: FC<Props> = ({ initialDate, initialView, children }) => {
	const [isReady, setIsReady] = useState(false);
	const [profileLimitsTimedOut, setProfileLimitsTimedOut] = useState(false);
	const [state, setState] = useState<InitializationState>({
		isReady: false,
		initialDate: moment().startOf("day"),
		initialCalendarType: CalendarType.WEEK,
		hasExplicitAttributes: false,
	});

	const userId = useAuthUser((state) => state.user?.uid);
	const profileLimits = useProfileLimits((state) => state.items);
	const isProfileLimitsInitializing = useProfileLimits((state) => state.isInitializing);

	// Initial setup effect
	useEffect(() => {
		devLog.log("[InitializationProvider] State check:", {
			userId,
			isProfileLimitsInitializing,
			profileLimitsCount: profileLimits?.length,
			isReady
		});
		
		if (isReady) return; // Already initialized
		
		let timeoutId: NodeJS.Timeout;
		
		// Set a timeout to prevent blocking the app indefinitely
		timeoutId = setTimeout(() => {
			if (!isReady) {
				devLog.warn("[InitializationProvider] Profile limits loading timeout after 15 seconds - using safe defaults");
				setProfileLimitsTimedOut(true);
				initializeState(true); // Pass timeout flag
			}
		}, PROFILE_LIMITS_TIMEOUT);

		// Check if we can initialize
		if (userId && !isProfileLimitsInitializing) {
			devLog.log("[InitializationProvider] Ready to initialize - profile limits loaded");
			initializeState(false); // Pass timeout flag as false
			clearTimeout(timeoutId);
		}

		return () => clearTimeout(timeoutId);
	}, [userId, isProfileLimitsInitializing, profileLimits, isReady]);

	// Handle prop changes effect
	useEffect(() => {
		if (isReady && userId && !isProfileLimitsInitializing) {
			devLog.log("[InitializationProvider] Props changed, re-initializing:", { initialDate, initialView });
			initializeState(profileLimitsTimedOut);
		}
	}, [initialDate, initialView, profileLimitsTimedOut]);

	const resetDayViewLocalStorage = () => {
		// Reset localStorage settings that are only accessible in day view
		// This prevents users from being stuck with filters they can't change
		localStorage.removeItem(LocalStorageKey.SHOW_ONLY_SCHEDULED_MEMBERS);
		devLog.log("[InitializationProvider] Reset day view localStorage settings due to lost access");
	};

	const initializeState = (isTimeout: boolean = false) => {
		const hasExplicitAttributes = !!(initialDate || initialView);
		devLog.log("[InitializationProvider] Initializing with:", { initialDate, initialView, hasExplicitAttributes, isTimeout });
		
		// If we timed out, log a warning about using safe defaults
		if (isTimeout) {
			devLog.warn("[InitializationProvider] Using safe defaults due to timeout - all premium features disabled");
		}
		
		// Parse initial date
		let selectedDate = moment().startOf("day");
		if (initialDate) {
			const parsedDate = moment(initialDate, 'YYYY-MM-DD', true);
			if (parsedDate.isValid()) {
				selectedDate = parsedDate.startOf("day");
				devLog.log("[InitializationProvider] Parsed date:", selectedDate.format('YYYY-MM-DD'));
			} else {
				devLog.warn("[InitializationProvider] Invalid date format:", initialDate);
			}
		}

		// Determine calendar type - Priority: 1) Attributes, 2) Settings, 3) Default
		let calendarType: CalendarType;
		
		if (hasExplicitAttributes) {
			// Priority 1: Use explicit attributes
			if (initialView) {
				// Explicit view provided
				if (initialView === 'day') {
					calendarType = CalendarType.DAY;
				} else if (initialView === 'week') {
					calendarType = CalendarType.WEEK;
				} else if (initialView === 'month') {
					calendarType = CalendarType.MONTH;
				} else {
					// Invalid view attribute, fall back to settings
					devLog.warn("[InitializationProvider] Invalid view attribute:", initialView);
					calendarType = getDefaultCalendarView();
				}
				devLog.log("[InitializationProvider] Using explicit view attribute:", CalendarType[calendarType]);
			} else if (initialDate) {
				// Date provided but no view - prefer day view if available, otherwise use settings
				// When timeout occurs, profileLimits might be empty/undefined, so we default to no access
				// Also, if userId is missing (auth failure), we can't check permissions
				const dayViewEnabled = isTimeout || !userId ? false : getIsDayViewEnabled(userId, profileLimits);
				
				if (isTimeout) {
					devLog.log("[InitializationProvider] Profile limits timeout - defaulting day view to disabled for safety");
				} else if (!userId) {
					devLog.criticalError("[InitializationProvider] CRITICAL: No userId available - authentication may have failed");
				} else {
					devLog.log("[InitializationProvider] Profile limits loaded - day view enabled:", dayViewEnabled);
				}
				
				if (dayViewEnabled) {
					calendarType = CalendarType.DAY;
					devLog.log("[InitializationProvider] Using day view for explicit date");
				} else {
					calendarType = getDefaultCalendarView();
					devLog.log("[InitializationProvider] Day view not available, using settings preference:", CalendarType[calendarType]);
				}
			} else {
				// Only view provided, no date
				calendarType = getDefaultCalendarView();
				devLog.log("[InitializationProvider] Only view provided, using settings for type");
			}
		} else {
			// Priority 2: Use settings-based preference (no attributes provided)
			calendarType = getDefaultCalendarView();
			
			// Validate settings preference against profile limits
			if (calendarType === CalendarType.DAY) {
				const dayViewEnabled = isTimeout || !userId ? false : getIsDayViewEnabled(userId, profileLimits);
				
				if (isTimeout) {
					devLog.log("[InitializationProvider] Cannot verify day view access due to timeout");
				} else {
					devLog.log("[InitializationProvider] Day view access check:", { dayViewEnabled, profileLimitsCount: profileLimits?.length });
				}
				
				if (!dayViewEnabled) {
					devLog.log("[InitializationProvider] Settings preference was DAY view but user doesn't have access, falling back to WEEK");
					calendarType = CalendarType.WEEK;
				}
			}
			devLog.log("[InitializationProvider] Using settings-based view:", CalendarType[calendarType]);
		}

		// Final validation: ensure day view is available if selected
		if (calendarType === CalendarType.DAY) {
			const dayViewEnabled = isTimeout || !userId ? false : getIsDayViewEnabled(userId, profileLimits);
			if (!dayViewEnabled) {
				devLog.warn("[InitializationProvider] Day view selected but not available, falling back to WEEK");
				calendarType = CalendarType.WEEK;
			}
		}
		
		// Clean up day view localStorage if user no longer has access
		// This must be done after we've determined the final calendar type and user permissions
		if (!isTimeout && userId) {
			const dayViewEnabled = getIsDayViewEnabled(userId, profileLimits);
			if (!dayViewEnabled) {
				resetDayViewLocalStorage();
			}
		}

		devLog.log("[InitializationProvider] Final state:", { 
			date: selectedDate.format('YYYY-MM-DD'), 
			calendarType: CalendarType[calendarType], 
			hasExplicitAttributes 
		});
		
		setState({
			isReady: true,
			initialDate: selectedDate,
			initialCalendarType: calendarType,
			hasExplicitAttributes,
		});
		setIsReady(true);
	};

	const getDefaultCalendarView = (): CalendarType => {
		// Only use the settings-based preference, not the last selected view
		try {
			const storedValue = localStorage.getItem("planning-board-default-calendar-view");
			devLog.log("[InitializationProvider] Stored calendar view setting:", storedValue);
			if (storedValue) {
				const parsedValue = JSON.parse(storedValue);
				if (Object.values(CalendarType).includes(parsedValue)) {
					devLog.log("[InitializationProvider] Using settings preference:", CalendarType[parsedValue]);
					return parsedValue as CalendarType;
				}
			}
		} catch (error) {
			devLog.error("Error reading default calendar view from settings:", error);
		}
		devLog.log("[InitializationProvider] No valid settings preference, defaulting to WEEK");
		return CalendarType.WEEK;
	};

	if (!isReady) {
		return <LoadingIndicator />;
	}

	return (
		<InitializationContext.Provider value={state}>
			{children}
		</InitializationContext.Provider>
	);
};

export const useInitialization = () => {
	const context = useContext(InitializationContext);
	if (!context) {
		throw new Error("useInitialization must be used within InitializationProvider");
	}
	return context;
}; 